import React, { useCallback, useState } from 'react';

import delv from 'dlv';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';

import { IOffer } from '@rbi-ctg/menu';
import ConfirmDialog from 'components/confirm-dialog';
import { MenuObjectTypes } from 'enums/menu';
import useLocation from 'hooks/location/use-location';
import AuthRequired from 'pages/authentication/auth-required';
import { useAuthContext } from 'state/auth';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { OfferAvailableRedemptionMethods } from 'state/launchdarkly/variations';
import { RedemptionMethod, useOffersContext } from 'state/offers';
import { ILockedOffer } from 'state/offers/hooks/use-locked-offers';
import { UIPattern } from 'state/offers/types';
import { useStoreContext } from 'state/store';
import { HapticsNotificationType, hapticNotification } from 'utils/haptic';
import { allowsUnauthenticatedRedemption } from 'utils/offers';
import { routes } from 'utils/routing';

import RedemptionLockedOfferModal from './redemption-locked-offer-dialog';
import RedemptionMethodDialog from './redemption-method-dialog';
import RedemptionModal from './redemption-modal';

interface IOfferRedemptionModalProps {
  modalIsOpen: boolean;
  closeModal(): void;
}

const TEMPORARY_DUMMY_OPTION_ID_FOR_RESTAURANT_ONLY = 'Lj4sBINzEO93P6iQCkyFCf';

const OfferRedemptionModal = ({ closeModal, modalIsOpen }: IOfferRedemptionModalProps) => {
  const { isStoreOpenAndAvailable } = useStoreContext();
  const location = useLocation();
  const { isAuthenticated } = useAuthContext();
  const [redemptionConfirmed, setRedemptionConfirmed] = useState(false);
  const [redemptionStartTime, setRedemptionStartTime] = useState(0);
  const [offerToRedeem, setOfferToRedeem] = useState<IOffer | null>(null);
  const {
    onCancelRedemption,
    onConfirmRedemption,
    onSelectMobileRedemption,
    onSelectRestaurantRedemption,
    redemptionMethod,
    selectedOffer,
  } = useOffersContext();
  const enableOfferRedemptionMethods = useFlag(LaunchDarklyFlag.ENABLE_OFFER_REDEMPTION_METHODS);
  const ruleSet = delv(offerToRedeem!, 'ruleSet');
  const isLoggedIn = isAuthenticated();
  const { formatMessage } = useIntl();
  const heading = formatMessage({ id: 'confirmDialogHeader' }, { br: <br /> });
  const body = formatMessage({ id: 'confirmDialogBody' });
  const navigate = useNavigate();

  const handleMobileSelection = useCallback(() => {
    if (!selectedOffer || !selectedOffer.option || !selectedOffer.vendorConfigs) {
      return closeModal();
    }

    if (!isLoggedIn) {
      // @TODO: implement query parameter to return to selected coupon once user is authenticated
      navigate(routes.signUp);

      return;
    }

    onSelectMobileRedemption();

    const {
      option: { _id, _type },
      vendorConfigs,
    } = selectedOffer;

    const urlOfferPluParams = Object.keys(vendorConfigs).reduce((urlParams, vendor) => {
      urlParams.append(`offer`, `${vendor}-${JSON.stringify(vendorConfigs[vendor])}`);
      return urlParams;
    }, new URLSearchParams(''));

    let menuModalUrl = `/menu/${_type}-${_id}?${urlOfferPluParams}`;

    // Navigate to the cart if the offer is a cart discount
    if (_type === MenuObjectTypes.OFFER_DISCOUNT) {
      menuModalUrl = routes.cart;
    }

    if (isStoreOpenAndAvailable) {
      navigate(menuModalUrl);
    } else {
      location.setStoreLocatorCallbackUrl(menuModalUrl);
      navigate(routes.storeLocator);
    }
  }, [
    selectedOffer,
    isLoggedIn,
    onSelectMobileRedemption,
    isStoreOpenAndAvailable,
    closeModal,
    navigate,
    location,
  ]);
  const redemptionMethodsDisabled =
    enableOfferRedemptionMethods === OfferAvailableRedemptionMethods.DISABLE_ALL;
  const inRestaurantOffersDisabled =
    redemptionMethodsDisabled ||
    enableOfferRedemptionMethods === OfferAvailableRedemptionMethods.ENABLE_MOBILE_ONLY;
  const mobileOffersDisabled =
    redemptionMethodsDisabled ||
    enableOfferRedemptionMethods === OfferAvailableRedemptionMethods.ENABLE_RESTAURANT_ONLY;

  const onConfirmRestaurantRedemption = useCallback(async () => {
    // @todo handle error and do not set redemption confirmed true
    // when onConfirmRedemption fails
    const confirmedOffer = selectedOffer!;
    await onConfirmRedemption(confirmedOffer);
    setRedemptionConfirmed(true);
    setRedemptionStartTime(Date.now());
    setOfferToRedeem(confirmedOffer);
  }, [onConfirmRedemption, selectedOffer]);

  const onCompleteRestaurantRedemption = useCallback(() => {
    closeModal();
    setRedemptionConfirmed(false);
    setOfferToRedeem(null);
  }, [closeModal]);

  // Check if offer requires "Log in" and optionally wrap with <AuthRequired>
  const offerRequiresLogin = !allowsUnauthenticatedRedemption(
    (selectedOffer && selectedOffer.ruleSet) || ruleSet
  );

  const OptionalAuth = offerRequiresLogin ? AuthRequired : React.Fragment;

  const onPreRedemptionModalsDismiss = () => {
    onCancelRedemption();
    closeModal();
  };

  if (!modalIsOpen) {
    return null;
  }

  const lockedOfferPatterns = [UIPattern.STEPPER, UIPattern.LIST];
  if (lockedOfferPatterns.find(pattern => pattern === selectedOffer?.uiPattern)) {
    return (
      <RedemptionLockedOfferModal offer={selectedOffer as ILockedOffer} onDismiss={closeModal} />
    );
  }

  if (offerToRedeem && redemptionConfirmed) {
    return (
      <OptionalAuth>
        <RedemptionModal
          onDismiss={onCompleteRestaurantRedemption}
          redemptionStartTime={redemptionStartTime}
          selectedOffer={offerToRedeem}
        />
      </OptionalAuth>
    );
  }
  if (!redemptionMethod && selectedOffer) {
    return (
      <RedemptionMethodDialog
        isMobileAvailable={
          !mobileOffersDisabled &&
          selectedOffer?.option?._id !== TEMPORARY_DUMMY_OPTION_ID_FOR_RESTAURANT_ONLY
        }
        isRestaurantAvailable={!inRestaurantOffersDisabled}
        onClickMobile={handleMobileSelection}
        onClickRestaurant={onSelectRestaurantRedemption}
        onDismiss={onPreRedemptionModalsDismiss}
        ruleSet={selectedOffer ? selectedOffer.ruleSet : null}
        shortCode={selectedOffer ? selectedOffer.shortCode : null}
        offer={selectedOffer}
      />
    );
  }

  // exit if in restaurant offers aren't enabled
  if (redemptionMethod === RedemptionMethod.mobile || inRestaurantOffersDisabled) {
    return null;
  }

  return (
    <OptionalAuth>
      <ConfirmDialog
        body={body}
        confirmLabel={formatMessage({ id: 'redeem' })}
        heading={heading}
        onCancel={onPreRedemptionModalsDismiss}
        onConfirm={() => {
          hapticNotification({ type: HapticsNotificationType.SUCCESS });
          onConfirmRestaurantRedemption();
        }}
        onDismiss={onPreRedemptionModalsDismiss}
        modalAppearanceEventMessage="Confirmation: User in restaurant to redeem offer"
      />
    </OptionalAuth>
  );
};

export default OfferRedemptionModal;
